import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useOutletContext, useParams } from 'react-router-dom';
import { useMemberAuth } from '../../../contexts/MemberAuthContext';
import { mfetch, MemberApiError } from '../api';
import Icon from '../design/Icon';
import { Spark, fmtDateShort, todayStr } from '../design/atoms';

/* SPEC 10 Metric detail — big chart, range chips, stat grid, recent entries. */

interface OutletCtx {
  activeGymId: string | null;
  hasClassScheduling: boolean;
  loading: boolean;
}

interface MetricDef {
  id: string;
  metricKey: string;
  name: string;
  category: string | null;
  unit: string | null;
  direction: string; // 'increase' | 'decrease'
  step: number;
}

interface MetricStats {
  latest: { value: number; loggedOn: string; isPr: boolean } | null;
  personalBest: { value: number; loggedOn: string } | null;
  avg30: number | null;
  delta30: number | null;
  logs30: number;
  consistency30: number | null;
  nextTarget: number | null;
}

interface DetailData {
  definition: MetricDef;
  isPinned: boolean;
  range: string;
  stats: MetricStats;
  series: { date: string; value: number }[];
  recent: { date: string; value: number; source: string; isPr: boolean }[];
}

type Range = '1m' | '3m' | '6m' | '1y' | 'all';
const RANGES: { key: Range; label: string }[] = [
  { key: '1m', label: '1M' },
  { key: '3m', label: '3M' },
  { key: '6m', label: '6M' },
  { key: '1y', label: '1Y' },
  { key: 'all', label: 'All' },
];

function fmtNum(v: number): string {
  return v % 1 === 0 ? String(v) : v.toFixed(1);
}

/** Signed delta with a true minus sign: -1.2 → "−1.2", 2 → "+2" */
function fmtDelta(d: number): string {
  return `${d < 0 ? '−' : '+'}${fmtNum(Math.abs(d))}`;
}

export default function MemberMetricDetailPage() {
  const { token } = useMemberAuth();
  const { activeGymId, loading: gymsLoading } = useOutletContext<OutletCtx>();
  const { metricId } = useParams<{ metricId: string }>();
  const navigate = useNavigate();

  const [range, setRange] = useState<Range>('3m');
  const [data, setData] = useState<DetailData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    if (!token || !metricId) return;
    setError(null);
    mfetch<{ data: DetailData }>(token, `/member/metrics/${metricId}`, {
      params: { range, date: todayStr() },
    })
      .then(r => setData(r.data))
      .catch(e => setError(e instanceof MemberApiError ? e.message : 'Could not load this metric'))
      .finally(() => setLoading(false));
  }, [token, metricId, range]);

  useEffect(load, [load]);

  if (!gymsLoading && !activeGymId) {
    return (
      <div className="mpage">
        <div className="dp dp-l" style={{ paddingTop: 8 }}>Progress</div>
        <div className="card flat"><div className="sub wrap">Join a gym to start tracking your progress.</div></div>
      </div>
    );
  }

  const def = data?.definition;
  const stats = data?.stats;
  const unit = def?.unit ? ` ${def.unit}` : '';
  const higherIsBetter = def ? def.direction !== 'decrease' : true;
  const deltaGood = (d: number) => (higherIsBetter ? d > 0 : d < 0);

  return (
    <div className="mpage">
      {/* Header */}
      <div className="row" style={{ paddingTop: 8, alignItems: 'flex-start' }}>
        <button className="icon-btn" aria-label="Back" onClick={() => navigate(-1)}>
          <Icon n="left" s={18} c="var(--ink-2)" />
        </button>
        {def ? (
          <div className="col" style={{ gap: 4, minWidth: 0, flex: 1 }}>
            {def.category && <div className="lbl">{def.category}</div>}
            <div className="dp dp-m trunc">{def.name}</div>
            {stats?.latest?.isPr && (
              <span className="tag gold" style={{ alignSelf: 'flex-start' }}>Personal record</span>
            )}
          </div>
        ) : (
          <div className="skel" style={{ height: 38, flex: 1 }} />
        )}
      </div>

      {loading ? (
        <>
          <div className="skel" style={{ height: 200 }} />
          <div className="grid2">
            {[0, 1, 2, 3].map(i => <div key={i} className="skel" style={{ height: 64 }} />)}
          </div>
          <div className="skel" style={{ height: 160 }} />
        </>
      ) : error ? (
        <div className="card flat">
          <div className="sub wrap">{error}</div>
          <button className="btn ghost sm" style={{ marginTop: 10 }} onClick={() => { setLoading(true); load(); }}>
            Retry
          </button>
        </div>
      ) : data && def && stats ? (
        <>
          {/* Chart card */}
          <div className="card">
            <div className="row" style={{ gap: 8, flexWrap: 'wrap', alignItems: 'baseline' }}>
              <span className="num" style={{ fontSize: 36 }}>
                {stats.latest ? fmtNum(stats.latest.value) : '—'}
              </span>
              {stats.latest && def.unit && <span className="sub">{def.unit}</span>}
              {stats.delta30 != null && stats.delta30 !== 0 && (
                <span className={`tag${deltaGood(stats.delta30) ? ' sage' : ' clay'}`}>
                  {fmtDelta(stats.delta30)} in 30 days
                </span>
              )}
            </div>
            {stats.latest ? (
              <div style={{ marginTop: 12 }}>
                {data.series.length >= 2 ? (
                  <Spark values={data.series.map(p => p.value)} h={92} w={340} fill />
                ) : (
                  <div className="tiny" style={{ height: 92, display: 'flex', alignItems: 'center' }}>
                    Log a few more days to see the trend
                  </div>
                )}
              </div>
            ) : (
              <div className="tiny" style={{ marginTop: 8 }}>No entries yet</div>
            )}
            <div className="divider" style={{ margin: '14px 0 12px' }} />
            <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
              {RANGES.map(r => (
                <button
                  key={r.key}
                  className={`chip${range === r.key ? ' on' : ''}`}
                  onClick={() => setRange(r.key)}
                >
                  {r.label}
                </button>
              ))}
            </div>
          </div>

          {/* Stat grid */}
          <div className="grid2">
            <div className="field">
              <span className="lbl">Personal best</span>
              <b className="num">{stats.personalBest ? `${fmtNum(stats.personalBest.value)}${unit}` : '—'}</b>
              <span className="tiny">{stats.personalBest ? fmtDateShort(stats.personalBest.loggedOn) : 'No logs yet'}</span>
            </div>
            <div className="field">
              <span className="lbl">30-day avg</span>
              <b className="num">{stats.avg30 != null ? `${fmtNum(stats.avg30)}${unit}` : '—'}</b>
              <span className="tiny">{stats.logs30} log{stats.logs30 === 1 ? '' : 's'}</span>
            </div>
            <div className="field">
              <span className="lbl">Consistency</span>
              <b className="num">{stats.consistency30 != null ? `${stats.consistency30}%` : '—'}</b>
              <span className="tiny">of days</span>
            </div>
            <div className="field">
              <span className="lbl">Next target</span>
              <b className="num">{stats.nextTarget != null ? `${fmtNum(stats.nextTarget)}${unit}` : '—'}</b>
              <span className="tiny">{higherIsBetter ? 'PB +10%' : 'PB −10%'}</span>
            </div>
          </div>

          {/* Recent entries */}
          <div className="lbl" style={{ padding: '2px 2px 0' }}>Recent entries</div>
          {data.recent.length > 0 ? (
            <div className="card" style={{ paddingTop: 4, paddingBottom: 4 }}>
              {data.recent.map(e => (
                <div key={`${e.date}-${e.value}`} className="lrow">
                  <span className="sub" style={{ flex: 1 }}>{fmtDateShort(e.date)}</span>
                  {e.isPr && <span className="tag gold">PR</span>}
                  <span className="num" style={{ fontSize: 15 }}>{fmtNum(e.value)}{unit}</span>
                </div>
              ))}
            </div>
          ) : (
            <div className="card flat"><div className="sub wrap">No entries yet.</div></div>
          )}

          <button className="btn gold block" onClick={() => navigate('/app/progress/log')}>
            Log {def.name}
          </button>
        </>
      ) : null}
    </div>
  );
}
